/**
 * RepresentantsManager — gestion des représentants d'une association (mairie).
 *
 * Affiché dans un Dialog ouvert depuis la fiche. Permet à la mairie de :
 *   - voir les représentants (validés + demandes en attente)
 *   - valider / refuser une demande
 *   - ajouter directement un représentant par email (compte existant)
 *   - changer la fonction (via ajout réécrivant la fonction) / retirer
 *
 * Réservé au staff gestion (admin / manager). L'agent ne voit pas ce bouton.
 */

import { useEffect, useState } from 'react';
import { Check, X, Trash2, UserPlus } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Button, Field, Input, Alert, Badge } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { REPRESENTANT_FONCTIONS } from '@roronoa-collectivites/shared-types';
import type { AssociationRepresentant, RepresentantFonction } from '@roronoa-collectivites/shared-types';

function fonctionLabel(v: string): string {
  return REPRESENTANT_FONCTIONS.find((f) => f.value === v)?.label ?? v;
}

export function RepresentantsManager({ associationId, associationNom }: { associationId: string; associationNom: string }) {
  const { toast } = useToast();
  const { confirm, dialogElement } = useConfirmDialog();
  const [reps, setReps] = useState<AssociationRepresentant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [fonction, setFonction] = useState<RepresentantFonction>('president');
  const [busy, setBusy] = useState(false);

  async function charger() {
    setLoading(true); setError(null);
    try {
      const detail = await api.associations.get(associationId);
      setReps(detail.representants ?? []);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { charger(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [associationId]);

  async function ajouter() {
    if (!email.trim()) { setError('Renseignez l\'email du représentant.'); return; }
    setBusy(true); setError(null);
    try {
      await api.associations.ajouterRepresentant(associationId, email.trim(), fonction);
      toast.success('Représentant ajouté');
      setEmail('');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur');
    } finally { setBusy(false); }
  }

  async function statuer(rep: AssociationRepresentant, decision: 'valide' | 'refuse') {
    try {
      await api.associations.statuerRepresentant(associationId, rep.id, decision);
      toast.success(decision === 'valide' ? 'Demande validée' : 'Demande refusée');
      await charger();
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    }
  }

  async function retirer(rep: AssociationRepresentant) {
    const ok = await confirm({
      title: 'Retirer le représentant',
      description: `Retirer ${rep.userPrenom} ${rep.userNom} de « ${associationNom} » ?`,
      confirmLabel: 'Retirer', danger: true,
    });
    if (!ok) return;
    try {
      await api.associations.retirerRepresentant(associationId, rep.id);
      toast.success('Représentant retiré');
      await charger();
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    }
  }

  const enAttente = reps.filter((r) => r.statut === 'en_attente');
  const valides = reps.filter((r) => r.statut === 'valide');

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      {/* Ajout direct par la mairie */}
      <div className="rounded-r-lg border border-border bg-surface/40 p-3">
        <div className="mb-2 text-sm font-medium text-ink">Ajouter un représentant</div>
        <p className="mb-3 text-xs text-ink-subtle">
          L'habitant doit déjà avoir un compte sur la plateforme. Saisissez son email.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto]">
          <Field label="Email du compte" htmlFor="rep-email">
            <Input id="rep-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="habitant@email.fr" />
          </Field>
          <Field label="Fonction" htmlFor="rep-fonction">
            <select id="rep-fonction"
              className="h-9 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso"
              value={fonction} onChange={(e) => setFonction(e.target.value as RepresentantFonction)}>
              {REPRESENTANT_FONCTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </Field>
          <div className="flex items-end">
            <Button variant="asso" onClick={ajouter} disabled={busy}>
              <UserPlus className="h-4 w-4" /> Ajouter
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-ink-subtle">Chargement…</div>
      ) : (
        <>
          {/* Demandes en attente */}
          {enAttente.length > 0 && (
            <div>
              <div className="mb-2 text-sm font-medium text-ink">Demandes en attente ({enAttente.length})</div>
              <div className="space-y-2">
                {enAttente.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-2 rounded-r-md border border-border bg-surface/40 p-2.5">
                    <div className="min-w-0">
                      <div className="truncate text-sm text-ink">{r.userPrenom} {r.userNom}</div>
                      <div className="truncate text-xs text-ink-subtle">{r.userEmail} · {fonctionLabel(r.fonction)}</div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button variant="ghost" size="sm" onClick={() => statuer(r, 'valide')} className="text-success hover:bg-success/10 hover:text-success">
                        <Check className="h-3.5 w-3.5" /> Valider
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => statuer(r, 'refuse')} className="text-urgent hover:bg-urgent/10 hover:text-urgent">
                        <X className="h-3.5 w-3.5" /> Refuser
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Représentants validés */}
          <div>
            <div className="mb-2 text-sm font-medium text-ink">Représentants ({valides.length})</div>
            {valides.length === 0 ? (
              <div className="rounded-r-md border border-border bg-surface/40 p-3 text-sm text-ink-subtle">
                Aucun représentant validé pour le moment.
              </div>
            ) : (
              <div className="space-y-2">
                {valides.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-2 rounded-r-md border border-border bg-surface/40 p-2.5">
                    <div className="min-w-0">
                      <div className="truncate text-sm text-ink">
                        {r.userPrenom} {r.userNom} <Badge variant="asso">{fonctionLabel(r.fonction)}</Badge>
                      </div>
                      <div className="truncate text-xs text-ink-subtle">{r.userEmail}</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => retirer(r)} className="shrink-0 text-urgent hover:bg-urgent/10 hover:text-urgent">
                      <Trash2 className="h-3.5 w-3.5" /> Retirer
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {dialogElement}
    </div>
  );
}
